import { isAppError } from '@/lib/api/errors';
import type { FieldError } from '@/lib/api/types';

/**
 * Helpers for the CMS-driven-collections governance gates (contract §4). The backend rejects
 * unsafe lifecycle transitions with structured field errors; these read them out so the editors
 * can give a non-technical user a clear, specific message instead of a generic "validation
 * error" — and know exactly which field to fix.
 */

function details(error: unknown): FieldError[] {
  return isAppError(error) ? (error.details ?? []) : [];
}

/** True when any field error carries the given backend code (e.g. `required_for_ready`). */
export function hasErrorCode(error: unknown, code: string): boolean {
  return details(error).some((d) => d.code === code);
}

/** Arabic copy gate (`locale_incomplete`) tripped on publish. */
export function isLocaleIncomplete(error: unknown): boolean {
  return hasErrorCode(error, 'locale_incomplete');
}

/** Field keys missing for a `ready` family (`required_for_ready`), e.g. `hero_image_id`. */
export function requiredForReadyFields(error: unknown): string[] {
  return details(error)
    .filter((d) => d.code === 'required_for_ready')
    .map((d) => d.field);
}

/** A reserved-slug rejection (`reserved`) — the slug shadows a system route. */
export function isReservedSlug(error: unknown): boolean {
  return hasErrorCode(error, 'reserved');
}

/**
 * Product slugs blocking a family delete (block-and-warn cascade, §3.4). The backend returns a
 * `409 conflict` listing them in the `products` field error; the message may be a single slug or
 * a comma/space-separated list, so we split defensively.
 */
export function blockingProductSlugs(error: unknown): string[] {
  if (!isAppError(error) || error.code !== 'conflict') return [];
  return details(error)
    .filter((d) => d.field === 'products' || d.field.startsWith('products'))
    .flatMap((d) => d.message.split(/[,\s]+/))
    .map((s) => s.trim())
    .filter(Boolean);
}
